import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, } from "@mui/material";
import {
  StyledDarkRedInformationTypography,
  StyledInformationSpan,
  StyledMainBox,
} from "../../../components/commonComponents/StyledComponents/styled";
import { ArrowBack, ArrowForward, Close, Save, } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton, { DarkGreyButton } from "../../../components/commonComponents/Buttons/ActionButton";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import {
  fetchStoreLevelCompanies,
  checkUserDetailsFromCookie,
  fetchBrands,
} from "../../../api_calls/userManagement";
import WizardStepper from "../../../components/commonComponents/Progress/WizardStepper";
import GeneralInfo from "./WizardSteps/GeneralInfo";
import Visuals from "./WizardSteps/Visuals";
import { useFileStore } from "./WizardSteps/FileProvider";
import StoresList from "./WizardSteps/StoresList";
// loop_prioritization: fetchVisualLimit disabled for pre_prod deploy — inStore not ready
// import { fetchVisualLimit, resetLoopsData, saveLoopCampaign, setLoopCampaignInfo, validateLoopCampaignGeneralInfo, validateStoreSelection, validateVisuals } from "../../../api_calls/loopCampaign";
import { resetLoopsData, saveLoopCampaign, setLoopCampaignInfo, validateLoopCampaignGeneralInfo, validateStoreSelection, validateVisuals } from "../../../api_calls/loopCampaign";
import Review from "./WizardSteps/Review";
import Confirmation from "../../../components/commonComponents/Confirmation";

const AddLoopCampaign = ({ open, onClose, size, usedFor, closeModal, loopinfo }) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [showCancelConfirmation, setShowCancelConfirmationConfirmation] = React.useState(false);
  const wizardRef = React.useRef(null);

  const statusBasedReadOnly =
    typeof currentStateData.new_pic_data !== "undefined" &&
      typeof currentStateData.new_pic_data.pic_start_end_status !== "undefined" &&
      ["Completed"].includes(currentStateData.new_pic_data.pic_start_end_status)
      ? true
      : false;

  const checkUserDetails = checkUserDetailsFromCookie();
  let userDetailObj = {};
  if (checkUserDetails.success === true) {
    userDetailObj = checkUserDetails.user_detail;
  }

  // -------- new loop code -------- //
  const { acceptedFiles } = useFileStore();
  const [activeStep, setActiveStep] = React.useState(0);

  const stepLabels = [
    "General Information & Schedule",
    "Visuals",
    "Stores",
    "Review",
  ];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            {/* Step 1: General Information & Schedule */}
            <GeneralInfo usedFor={usedFor} closeModal={closeModal} loopinfo={loopinfo} fieldErrors={fieldErrors} />
          </>
        );
      case 1:
        return (
          <>
            {/* Step 2: Visuals */}
            <Visuals usedFor={usedFor} loopInfo={loopinfo} />
          </>
        );
      case 2:
        return (
          <>
            {/* Step 3: Stores */}
            <StoresList usedFor={usedFor} closeModal={onClose} loopinfo={loopinfo} wizardRef={wizardRef} />
          </>
        );
      case 3:
        return (
          <>
            {/* Step 4: Review */}
            <Review loopInfo={loopinfo} usedFor={usedFor} />
          </>
        );
      default:
        return <></>;
    }
  };

  const dummyValidate = () => {
    return {
      error: false
    }
  }

  // validate the current step before proceeding
  const validateCurrentStep = () => {
    const validateFunctions = {
      0: () => validateLoopCampaignGeneralInfo(usedFor),
      1: () => validateVisuals(acceptedFiles, usedFor),
      2: () => validateStoreSelection(),
      3: () => dummyValidate()
    }

    const validationFunction = validateFunctions[activeStep];
    const validationResult = validationFunction();

    if (validationResult.error === true) {
      setFieldErrors(validationResult.errorMessages);
      return {
        error_found: true
      }
    } else {
      return {
        error_found: false
      }
    }
  }

  const updateLoopData = (loopData) => {
    const selectedLoopData = typeof currentStateData.new_loop_data !== "undefined" ? currentStateData.new_loop_data : {};
    dispatchAction(
      updateStateData({
        new_loop_data: { ...selectedLoopData, ...loopData },
      })
    );
  };

  //=============================================================================================

  React.useEffect(() => {
    // set loop details (if picture info is passed.)
    if (typeof loopinfo !== "undefined" && usedFor === "update") {
      setLoopCampaignInfo(loopinfo);
    } else {
      if (userDetailObj.user_type === "cyreen" && usedFor !== "update") {
        if (typeof currentStateData.independent_owners_list !== "undefined") {
          let data = currentStateData.independent_owners_list.find((owner) => owner.label.includes("Cyreen"));
          if (data !== undefined) {
            updateLoopData({
              loop_owner: data.label,
              loop_owner_id: data.id,
              loop_owner_company_level: data.company_level,
              share_list: [],
              share_list_selection: [],
              visual_upload_limit: data.visual_upload_limit,
              visual_upload_duration: data.visual_upload_duration
            });
            fetchBrands(data.id);
            // loop_prioritization: disabled for pre_prod deploy — inStore not ready
            // fetchVisualLimit(data.id);
          }
        }
      }

      if (userDetailObj.user_type !== "cyreen" && usedFor !== "update") {
        fetchBrands();
        // loop_prioritization: disabled for pre_prod deploy — inStore not ready
        // fetchVisualLimit(userDetailObj.root_company_id);
        updateLoopData({
          visual_upload_limit: userDetailObj.loop_visual_limit,
          visual_upload_duration: userDetailObj.loop_visual_max_duration
        })
      }
    }
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => {
          e.preventDefault();
        }}
        fullWidth={false}
        maxWidth={false} // Disable automatic sizing
        sx={{
          "& .MuiDialog-paper": {
            width: "1320px",
            height: "850px",
            maxWidth: "none", // Override default maxWidth
          },
        }}
      // fullScreen
      >
        <DialogTitle> {usedFor === "update" ? "Edit : " + loopinfo?.name : "Add New Loop-based Campaign"} </DialogTitle>
        <DialogContent sx={{ overflowY: "auto" }} ref={wizardRef}>

          <StyledMainBox component="main" sx={{ padding: 0 }}>
            <WizardStepper
              activeStep={activeStep}
              stepLabels={stepLabels}
            // onStepClick={(stepIndex) => {
            //   if (usedFor === "update") {
            //     setActiveStep(stepIndex)
            //   }
            // }}
            />
            <Box height={20} />

            {getStepContent(activeStep)}

            {usedFor === "update" && (
              <>
                <Box height={20} />
                <StyledDarkRedInformationTypography variant="body2">
                  Last Modified:
                  <StyledInformationSpan
                    style={{
                      color: "#2D2D37",
                      fontWeight: "bold",
                      marginLeft: "5px",
                      marginTop: "3px",
                    }}
                  >
                    {loopinfo.last_edit_by}
                  </StyledInformationSpan>
                </StyledDarkRedInformationTypography>
              </>
            )}

          </StyledMainBox>
        </DialogContent>
        <Box height={20} />
        <DialogActions>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            width="100%"
          >
            <CloseButton
              onClick={(e) => {
                e.preventDefault();
                if (typeof currentStateData.new_loop_data !== "undefined" && Object.keys(currentStateData.new_loop_data).length > 0) {
                  setShowCancelConfirmationConfirmation(true);
                } else {
                  closeModal();
                }
              }}
              label="Cancel"
              icon={<Close />}
            />

            {/* Right side - Navigation buttons */}
            <Stack direction="row" spacing={2} alignItems="flex-end">
              {
                activeStep >= 1 && (
                  <DarkGreyButton
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveStep((prev) => prev - 1);
                    }}
                    label="Back"
                    icon={<ArrowBack />}
                  />
                )
              }

              <ActionButton
                onClick={(e) => {
                  e.preventDefault();
                  // validate the current step before proceeding
                  const validate = validateCurrentStep();
                  if (validate.error_found === false) {
                    setFieldErrors({});
                    if (activeStep === 3) {
                      saveLoopCampaign(usedFor, acceptedFiles).then(info => {
                        if (info.success === true) {
                          // reset filters and load pictures list again
                          resetLoopsData();
                          closeModal();
                        } else {
                          // redirect to store list (if there's error while saving/updating)
                          setActiveStep(2);
                        }
                      })
                    } else {
                      setActiveStep((prev) => prev + 1);
                    }
                  }
                }}
                label={activeStep === 3 ? "Save" : "Next"}
                icon={activeStep === 3 ? <Save /> : <ArrowForward />}
              />
            </Stack>
          </Stack>
          {/* <Stack direction={"row"} spacing={2}>
          </Stack> */}
        </DialogActions>
        {/* ------- cancel confirmation -------- */}
        {showCancelConfirmation === true && (
          <Confirmation
            showConfirmationPopup={showCancelConfirmation}
            closePopup={(e) => {
              setShowCancelConfirmationConfirmation(false);
            }}
            confirmationActionCall={() => {
              closeModal();
            }}
            confirmationMessage={
              <>
                <Typography variant="body2">
                  Are you certain you want to discard all the changes you have made?
                </Typography>
              </>
            }
            confirmationTitle={"Confirmation"}
          />
        )}
      </Dialog>

    </>
  );
};

export default AddLoopCampaign;
